import React from "react";
import { createPortal } from "react-dom";
import { useStickyTableHeader } from "../../hooks/useStickyTableHeader";

const parseTableElement = (children) => {
  if (!React.isValidElement(children) || children.type !== "table") {
    return null;
  }

  const { className, children: tableChildren, ...tableProps } = children.props;
  const parts = React.Children.toArray(tableChildren);
  const thead = parts.find(
    (child) => React.isValidElement(child) && child.type === "thead"
  );
  const body = parts.filter(
    (child) => !(React.isValidElement(child) && child.type === "thead")
  );

  return { className, tableProps, thead, body };
};

const StickyTable = ({
  children,
  refreshDeps = [],
  scrollClassName = "text-nowrap",
  wrapClassName = "",
}) => {
  const parsedTable = parseTableElement(children);
  const {
    tableWrapRef,
    tableScrollRef,
    tableRef,
    theadRef,
    fixedHeaderTableRef,
    tableScrollLeft,
    showStickyActionShadow,
    isHeaderFixed,
    fixedHeaderMetrics,
    handleTableScroll,
  } = useStickyTableHeader(refreshDeps);

  const scrollClasses = [
    "table-responsive",
    "sticky-table-body-scroll",
    showStickyActionShadow ? "sticky-action-shadow" : "",
    scrollClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const wrapClasses = [
    "sticky-table-wrap",
    showStickyActionShadow ? "sticky-action-shadow" : "",
    wrapClassName,
  ]
    .filter(Boolean)
    .join(" ");

  if (!parsedTable) {
    return children;
  }

  const { className, tableProps, thead, body } = parsedTable;
  const theadProps = thead?.props || {};

  return (
    <div ref={tableWrapRef} className={wrapClasses}>
      {isHeaderFixed &&
        createPortal(
          // Rendered via a portal straight to <body>: the glass chrome
          // (.glass-panel etc.) uses backdrop-filter, which makes any
          // position:fixed descendant resolve against that ancestor's box
          // instead of the viewport — this clone would otherwise render
          // shifted/misaligned instead of truly stuck to the viewport top.
          <div
            className="sticky-table-fixed-header"
            style={{
              position: "fixed",
              top: fixedHeaderMetrics.top,
              left: fixedHeaderMetrics.left,
              width: fixedHeaderMetrics.width,
              zIndex: 90,
              overflow: "hidden",
            }}
          >
            <table
              ref={fixedHeaderTableRef}
              className={className}
              style={{
                marginLeft: -tableScrollLeft,
                width: fixedHeaderMetrics.tableWidth,
                minWidth: fixedHeaderMetrics.tableWidth,
              }}
              {...tableProps}
            >
              <thead {...theadProps}>{theadProps.children}</thead>
            </table>
          </div>,
          document.body,
        )}
      <div
        ref={tableScrollRef}
        className={scrollClasses}
        onScroll={handleTableScroll}
      >
        <table ref={tableRef} className={className} {...tableProps}>
          <thead ref={theadRef} {...theadProps}>
            {theadProps.children}
          </thead>
          {body}
        </table>
      </div>
    </div>
  );
};

export default StickyTable;
